import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { api } from "../api/apiClient";
import type {
  ForwardDemandView,
  AssignableCourier,
  CustomerOrder,
  DeliveryJob,
  DeliveryProblem,
  AnalyticsHomeSummary,
  PayDayDue,
  Shift,
} from "../api/types";
import { HomeActions, type HomeActionItem } from "../components/home/HomeActions";
import { YourShiftCard, YourWeekCard, mondayOf } from "../components/home/MyWeekCards";
import { WasteTodayModal } from "../components/home/WasteTodayModal";
import { OrderDetailModal } from "../components/orders/OrderDetailModal";
import { StatTile } from "../components/ui/StatTile";
import { useAuth } from "../state/AuthContext";
import type { DashboardOutletContext } from "./dashboardOutletContext";
import { toShopRows, toShopTotals } from "../utils/homeShopRows";
import { businessMonthsAgo } from "../utils/businessDay";
import { localDayKey } from "../utils/dayKey";
import { eur, hhmm } from "../utils/homeFormat";
import { CARD_LAYOUT_MQ, useCardLayout } from "../utils/useCardLayout";
import {
  joinNotes,
  salesWithUnknownCostNote,
  unknownEntriesNotCountedNote,
} from "../utils/unknownCost";

/** "2026-08-26" → "Tue 26 Aug" for the attention row. */
function fmtShortDate(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

/** One person's day, for Today's team (G-24). */
interface TeamMember {
  userId: string;
  fullName: string;
  plannedStart: string;
  plannedEnd: string;
  actualStart: string | null;
  actualEnd: string | null;
  /** Past planned end with nobody having clocked out — G-19's signal. */
  overdue: boolean;
  /** WO-B (OD-198): null — today's sales or waste could not be loaded. */
  salesEur: number | null;
  faultWasteEur: number | null;
}

/** A calendar month, as the period control understands it. */
interface Period {
  year: number;
  month: number;
}

const monthLabel = (p: Period) =>
  new Date(p.year, p.month - 1, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });

const ORDER_STATUS_LABELS: Record<CustomerOrder["status"], string> = {
  NEW: "New",
  CONFIRMED: "Confirmed",
  IN_PREPARATION: "In preparation",
  READY: "Ready",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
};

const timeLabel = (order: CustomerOrder): string => {
  if (order.timeType === "ASAP" || !order.scheduledFor) return "ASAP";
  const start = new Date(order.scheduledFor).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (order.timeType === "WINDOW" && order.scheduledUntil) {
    const end = new Date(order.scheduledUntil).toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${start}–${end}`;
  }
  return start;
};

/** "Tuesday 4 November" for the page header. */
const longDate = (d: Date) =>
  d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });

/** The month's first and last calendar days, "YYYY-MM-DD", for a Sales records link. */
const monthDays = (p: Period) => ({
  from: `${p.year}-${String(p.month).padStart(2, "0")}-01`,
  to: `${p.year}-${String(p.month).padStart(2, "0")}-${String(new Date(p.year, p.month, 0).getDate()).padStart(2, "0")}`,
});

/**
 * The home page — a role-aware "Today" overview (G-9.3 item 3, owner IA
 * decisions 2026-07-28; redesigned as G-204, owner rulings 2026-11-04).
 * Owners and managers get what needs a person, today's figures, the per-shop
 * table and today's agenda; florists get their shift, what needs them and
 * the agenda. Nothing on the page repeats the sidebar: the shortcuts live
 * behind one floating "Actions" button while the sidebar is collapsed.
 */
export function MainPage() {
  const { locations } = useOutletContext<DashboardOutletContext>();
  const { user, hasRole } = useAuth();
  const seesMoney = hasRole("OWNER", "MANAGER");
  /** OD-174: running costs and the profit built on them are the owner's, not the manager's. */
  const seesCosts = hasRole("OWNER");
  const seesDispatch = hasRole("OWNER", "MANAGER");
  /** G-204: a manager's or florist's own shift and week open their home. */
  const seesOwnShifts = hasRole("MANAGER", "FLORIST");
  const assignedId = user?.assignedLocation?.id;
  const cardLayout = useCardLayout(CARD_LAYOUT_MQ);

  /**
   * `shops` is ALWAYS today — it feeds the tiles, which say "today" on them.
   * `scopedShops` is what the By-shop card renders and follows the picker.
   * They are the same object while the scope is "today"; keeping them separate
   * is what stops a month selection from silently rewriting tiles labelled
   * "Revenue today".
   */
  const [team, setTeam] = useState<TeamMember[] | null>(null);
  /**
   * G-169 (WO-D): every money figure on this page, in one server answer —
   * per shop and added once there (`GET /analytics/home`). `home.today`
   * feeds the tiles, which say "today" on them; `home.scope` is what the
   * By-shop card renders and follows the picker. A failed request makes
   * every figure "—" (WO-B, OD-198: unknown, never €0.00).
   */
  const [home, setHome] = useState<AnalyticsHomeSummary | null>(null);
  const [homeFailed, setHomeFailed] = useState(false);
  const [orders, setOrders] = useState<CustomerOrder[] | null>(null);
  const [pendingJobs, setPendingJobs] = useState<DeliveryJob[] | null>(null);
  const [unconfirmed, setUnconfirmed] = useState<AssignableCourier[]>([]);
  /**
   * What the "By shop" card is showing. The control sits in the card header,
   * so it must scope the WHOLE card — an earlier version drove only the
   * comparison line, which meant picking July printed July's average under
   * rows that still showed today's zeros (owner report 2026-08-01). A control
   * that looks like a filter must behave like one.
   *
   * "today" also keeps G-25 decision 2: today's figures are shown against the
   * average of a normal trading day in the current month, because a number
   * with no baseline is not information — "€0.00" is fine at 9 am and a
   * crisis at 6 pm.
   */
  const [scope, setScope] = useState<"today" | Period>("today");
  const [shopifyNewCount, setShopifyNewCount] = useState<number | null>(null);
  // G-131: albaranes and hand-made deliveries with no factura yet (server-scoped to the actor's shops).
  const [waitingFactura, setWaitingFactura] = useState<{ albaranes: number; deliveries: number } | null>(null);
  const [problems, setProblems] = useState<DeliveryProblem[]>([]);
  // G-25 decision 4, closable since G-16 shipped: today's order rows open
  // THE order-detail surface instead of pointing at the shop dashboard.
  const [detailOrder, setDetailOrder] = useState<CustomerOrder | null>(null);
  // G-45 part 3: unjustified below-floor sales — a Needs-attention row.
  const [belowFloorCount, setBelowFloorCount] = useState(0);
  /** G-166: Shopify refunds whose stock nobody checked yet (owner: all shops; manager: own). */
  const [stockToCheckCount, setStockToCheckCount] = useState(0);
  /** G-135: one-off expenses still without an invoice (owner only). */
  const [missingInvoiceCount, setMissingInvoiceCount] = useState(0);
  /** G-163: people whose pay day has come and who are still owed (owner only; the app never pays). */
  const [payDayDue, setPayDayDue] = useState<PayDayDue | null>(null);
  /** G-137: hand-made deliveries arrived with no document (owner: all shops; manager: own). */
  const [missingDocumentCount, setMissingDocumentCount] = useState(0);
  const [forwardDemand, setForwardDemand] = useState<ForwardDemandView | null>(
    null,
  );
  /** G-204: the person's own shifts this week (manager, florist). */
  const [myShifts, setMyShifts] = useState<Shift[] | null>(null);
  /** G-204: the "Waste" tile's sheet. */
  const [wasteOpen, setWasteOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scopeLocationIds = useMemo(() => {
    if (user?.role === "OWNER") {
      return locations.filter((l) => l.isActive).map((l) => l.id);
    }
    return assignedId ? [assignedId] : [];
  }, [user?.role, locations, assignedId]);

  const refresh = useCallback(async () => {
    setError(null);
    const today = new Date();
    const results = await Promise.allSettled([
      // Today's orders — the endpoint requires an explicit locationId for
      // owners, so query per active shop and merge.
      Promise.all(
        scopeLocationIds.map((locationId) =>
          api.listCustomerOrders({
            locationId,
            from: localDayKey(today),
            to: localDayKey(today),
          }),
        ),
      ).then((lists) => lists.flat()),
      // G-169: every money figure, per shop and added once on the server. The
      // card's figures follow `scope`: today, or the whole of a chosen month;
      // the tiles and the baseline are always today's (Barcelona).
      seesMoney && scopeLocationIds.length > 0
        ? api.getAnalyticsHome({
            locationIds: scopeLocationIds,
            ...(scope === "today" ? {} : { year: scope.year, month: scope.month }),
          })
        : Promise.resolve(null),
      // G-24 Today's team: who is in, planned vs actual.
      seesMoney && scopeLocationIds.length > 0
        ? Promise.all(
            scopeLocationIds.map((locationId) =>
              api.listShifts({ locationId, from: localDayKey(today), to: localDayKey(today) }),
            ),
          ).then((lists) => lists.flat())
        : Promise.resolve(null),
      // Dispatch signals (owner + manager).
      seesDispatch ? api.listDeliveryJobs({ status: "PENDING" }) : Promise.resolve(null),
      seesDispatch ? api.listAssignableCouriers() : Promise.resolve(null),
      seesDispatch ? api.listShopifyOrders({ status: "NEW" }) : Promise.resolve(null),
      // G-4 flares that still need a person (self-clearing when the job ends).
      seesDispatch ? api.listDeliveryProblems() : Promise.resolve(null),
      // G-45 part 3: below-floor sales still missing their reason.
      seesMoney ? api.getBelowFloorSales() : Promise.resolve(null),
      // G-43: the two-week forward look — which scheduled orders will fail.
      seesMoney ? api.getForwardDemand() : Promise.resolve(null),
      // G-131: what still waits for a factura — straight away, every day (owner ruling).
      seesDispatch ? api.listWaitingForFactura() : Promise.resolve(null),
      // G-135: one-off expenses with no invoice yet — the owner's to chase.
      seesCosts ? api.getExpensesMissingInvoiceCount() : Promise.resolve(null),
      // G-137 (OD-184): hand-made deliveries that arrived with no document.
      seesDispatch ? api.countDeliveriesMissingDocument() : Promise.resolve(null),
      // G-163: pay day passed and someone is still owed — a reminder, never a payment.
      seesCosts ? api.getPayDayDue() : Promise.resolve(null),
      // G-166 (owner 2026-10-20): Shopify refunds whose stock is still to check.
      seesMoney ? api.listRefundStockToCheck() : Promise.resolve(null),
      // G-204: the person's own week, Monday to Sunday (manager, florist).
      seesOwnShifts
        ? (() => {
            const monday = mondayOf(today);
            const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
            return api.listMyShifts({ from: localDayKey(monday), to: localDayKey(sunday) });
          })()
        : Promise.resolve(null),
    ]);

    const [
      ordersRes,
      homeRes,
      shiftsRes,
      pendingRes,
      couriersRes,
      shopifyRes,
      problemsRes,
      belowFloorRes,
      forwardRes,
      waitingFacturaRes,
      missingInvoiceRes,
      missingDocumentRes,
      payDayDueRes,
      stockToCheckRes,
      myShiftsRes,
    ] = results;
    if (stockToCheckRes.status === "fulfilled" && stockToCheckRes.value) {
      setStockToCheckCount(stockToCheckRes.value.length);
    }
    if (myShiftsRes.status === "fulfilled" && myShiftsRes.value) {
      setMyShifts(myShiftsRes.value);
    }

    if (ordersRes.status === "fulfilled") {
      setOrders(
        ordersRes.value
          .filter((o) => o.status !== "CANCELLED")
          .sort((a, b) =>
            (a.scheduledFor ?? a.scheduledDate).localeCompare(
              b.scheduledFor ?? b.scheduledDate,
            ),
          ),
      );
    } else {
      setError("Could not load today's orders.");
    }
    // G-169: the server answers per shop and added once; the page keeps nothing
    // but the answer. Everything labelled "today" reads `home.today`, whatever
    // the By-shop card's scope — getting this wrong silently prints last
    // month's numbers under a "today" heading (it happened three times while
    // building this page). "Today" is the server's Barcelona day, like every total.
    if (homeRes.status === "fulfilled") {
      setHome(homeRes.value);
      setHomeFailed(false);
    } else {
      setHome(null);
      setHomeFailed(true);
    }
    const todayFigures = homeRes.status === "fulfilled" ? homeRes.value?.today ?? null : null;

    // G-24 Today's team — the server's per-person figures for today (every
    // shop in scope together); unknown ("—") when the money could not load.
    if (shiftsRes.status === "fulfilled" && shiftsRes.value) {
      const now = Date.now();
      const sellers = new Map(todayFigures?.bySeller.map((r) => [r.userId, r]) ?? []);
      const fault = new Map(todayFigures?.faultWasteByActor.map((r) => [r.userId, r]) ?? []);
      setTeam(
        (shiftsRes.value as Shift[])
          .filter((sh) => sh.status !== "CANCELED")
          .map((sh) => ({
            userId: sh.user.id,
            fullName: sh.user.fullName,
            plannedStart: sh.plannedStart,
            plannedEnd: sh.plannedEnd,
            actualStart: sh.actualStart,
            actualEnd: sh.actualEnd,
            // Decision 3: expected end IS planned end, flagged when overdue.
            overdue: !sh.actualEnd && new Date(sh.plannedEnd).getTime() < now,
            salesEur: todayFigures ? Number(sellers.get(sh.user.id)?.totalEur ?? 0) : null,
            faultWasteEur: todayFigures ? Number(fault.get(sh.user.id)?.costEur ?? 0) : null,
          }))
          .sort((a, b) => a.plannedStart.localeCompare(b.plannedStart)),
      );
    }

    if (pendingRes.status === "fulfilled" && pendingRes.value) {
      setPendingJobs(pendingRes.value);
    }
    if (couriersRes.status === "fulfilled" && couriersRes.value) {
      setUnconfirmed(
        couriersRes.value.filter((c) => c.todayShift?.status === "PLANNED"),
      );
    }
    if (shopifyRes.status === "fulfilled" && shopifyRes.value) {
      setShopifyNewCount(shopifyRes.value.length);
    }
    if (belowFloorRes.status === "fulfilled" && belowFloorRes.value) {
      setBelowFloorCount(belowFloorRes.value.length);
    }
    if (forwardRes.status === "fulfilled" && forwardRes.value) {
      setForwardDemand(forwardRes.value);
    }
    if (missingInvoiceRes.status === "fulfilled" && missingInvoiceRes.value) {
      setMissingInvoiceCount(missingInvoiceRes.value.count);
    }
    if (missingDocumentRes.status === "fulfilled" && missingDocumentRes.value) {
      setMissingDocumentCount(missingDocumentRes.value.count);
    }
    if (payDayDueRes.status === "fulfilled") {
      setPayDayDue(payDayDueRes.value);
    }
    if (waitingFacturaRes.status === "fulfilled" && waitingFacturaRes.value) {
      setWaitingFactura({
        albaranes: waitingFacturaRes.value.albaranes,
        deliveries: waitingFacturaRes.value.deliveries,
      });
    }
    if (problemsRes.status === "fulfilled" && problemsRes.value) {
      setProblems(problemsRes.value);
    }
  }, [seesMoney, seesDispatch, seesCosts, seesOwnShifts, scopeLocationIds, scope]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * G-169: the tiles, the By-shop rows and the baseline are the server's own
   * figures (`GET /analytics/home`), read off one answer — nothing is added
   * here. Orders due per shop is a count the page already holds.
   */
  const ordersDueAt = useCallback(
    (locationId: string) =>
      (orders ?? []).filter((o) => o.status !== "CANCELLED" && o.location.id === locationId).length,
    [orders],
  );
  const scopedShops = useMemo(() => (home ? toShopRows(home.scope, ordersDueAt) : null), [home, ordersDueAt]);
  const totals = useMemo(() => (home ? toShopTotals(home.today) : null), [home]);
  /** The "All shops" row of the card: the card's own period, not today's. */
  const scopedTotals = useMemo(() => (home ? toShopTotals(home.scope) : null), [home]);
  const baseline = useMemo(
    () =>
      home?.baseline.averageEur != null
        ? { averageEur: Number(home.baseline.averageEur), tradingDays: home.baseline.tradingDays }
        : null,
    [home],
  );

  /**
   * G-45 / G-204 — the honest number: what is actually left today. The server
   * works it out per shop (that shop's contribution after VAT less that shop's
   * running costs) and the tile is the rows' sum (`totals.profitEur`, owner
   * 2026-11-04). KI-115: with no running cost on record the costs are unknown
   * (null), there is no profit, and the tile shows the after-VAT figure under
   * "before running costs" — never a fabricated zero-cost profit. A failed
   * load is different: the profit is then unknown ("—").
   */
  const profitToday = useMemo(() => {
    if (homeFailed) return { afterVatEur: null, costsEur: null, valueEur: null, hasCosts: false, costsFailed: true };
    if (!totals) return null;
    // G-168 (KI-115 wording): only running costs above zero were really taken off.
    const hasCosts = totals.runningCostsEur != null && totals.runningCostsEur > 0;
    return {
      afterVatEur: totals.contributionAfterVatEur,
      costsEur: totals.runningCostsEur,
      // No cost on record anywhere: the after-VAT figure under "before running
      // costs". Costs on record but a selling shop without any: unknown ("—").
      valueEur: hasCosts ? totals.profitEur : totals.contributionAfterVatEur,
      hasCosts,
      costsFailed: false,
    };
  }, [totals, homeFailed]);

  /** The card's own total — the share bar and its empty state must divide by
      what the card is SHOWING, not by today. */
  const scopedTotalRevenue = home ? Number(home.scope.totals.totalEur) : 0;

  /** Today's revenue against a normal trading day in the chosen period (the server's figure). */
  const revenueDelta = home?.baseline.revenueDeltaPercent ?? null;

  const ordersDueToday = orders?.length ?? null;
  const unpreparedOrders = useMemo(
    () =>
      (orders ?? []).filter(
        (o) => o.status === "NEW" || o.status === "CONFIRMED",
      ),
    [orders],
  );

  const attention: Array<{ key: string; text: string; to: string; cta: string }> =
    useMemo(() => {
      const rows: Array<{ key: string; text: string; to: string; cta: string }> = [];
      // A courier stuck mid-delivery outranks every other signal — it is the
      // only one where someone is waiting on the doorstep right now.
      for (const problem of problems) {
        rows.push({
          key: `problem-${problem.jobId}`,
          text: `${problem.courierName ?? "Courier"} reported a problem on ${problem.code}: ${problem.message}`,
          to: "/app/dispatch",
          cta: "Courier board",
        });
      }
      if (seesDispatch && pendingJobs && pendingJobs.length > 0) {
        rows.push({
          key: "pending-jobs",
          text: `${pendingJobs.length} ${pendingJobs.length === 1 ? "delivery needs" : "deliveries need"} a courier`,
          to: "/app/dispatch",
          cta: "Courier board",
        });
      }
      if (seesDispatch && unconfirmed.length > 0) {
        rows.push({
          key: "unconfirmed",
          text: `${unconfirmed
            .map((c) => c.fullName)
            .join(", ")} ${unconfirmed.length === 1 ? "has" : "have"} a planned day but ${
            unconfirmed.length === 1 ? "hasn't" : "haven't"
          } confirmed yet`,
          to: "/app/couriers",
          cta: "Couriers",
        });
      }
      if (seesDispatch && shopifyNewCount != null && shopifyNewCount > 0) {
        rows.push({
          key: "shopify",
          text: `${shopifyNewCount} new Shopify ${shopifyNewCount === 1 ? "order" : "orders"} waiting`,
          to: "/app/shopify-orders",
          cta: "Shopify",
        });
      }
      // G-25 decision 5 — an order with no agreed price is harmless today and
      // BLOCKING after G-20, whose handover builds the sale FROM that price.
      const unpriced = (orders ?? []).filter((o) => !o.totalEur);
      if (unpriced.length > 0) {
        rows.push({
          key: "unpriced",
          text: `${unpriced.length} of today's orders ${
            unpriced.length === 1 ? "has" : "have"
          } no agreed price yet`,
          to: "/app/orders",
          cta: "Orders",
        });
      }
      if (unpreparedOrders.length > 0) {
        rows.push({
          key: "unprepared",
          text: `${unpreparedOrders.length} of today's orders ${
            unpreparedOrders.length === 1 ? "is" : "are"
          } not in preparation yet`,
          to: "/app/orders",
          cta: "Orders",
        });
      }
      // G-131: an albarán counts no money until its factura is linked, and a
      // delivery typed by hand waits for its factura too.
      if (seesDispatch && waitingFactura && waitingFactura.albaranes + waitingFactura.deliveries > 0) {
        const parts = [
          waitingFactura.albaranes > 0
            ? `${waitingFactura.albaranes} ${waitingFactura.albaranes === 1 ? "albarán" : "albaranes"}`
            : null,
          waitingFactura.deliveries > 0
            ? `${waitingFactura.deliveries} ${
                waitingFactura.deliveries === 1 ? "delivery" : "deliveries"
              } typed by hand`
            : null,
        ].filter((p): p is string => p !== null);
        rows.push({
          key: "waiting-factura",
          text: `${parts.join(" and ")} waiting for a factura`,
          to: "/app/invoices?view=waiting",
          cta: "Link",
        });
      }
      // G-137 (OD-184): flagged, never blocking — a hand-made delivery's own paper.
      if (seesDispatch && missingDocumentCount > 0) {
        rows.push({
          key: "deliveries-no-document",
          text: `${missingDocumentCount} ${
            missingDocumentCount === 1 ? "delivery" : "deliveries"
          } typed by hand ${missingDocumentCount === 1 ? "has" : "have"} no document`,
          to: "/app/deliveries?documents=missing",
          cta: "Add",
        });
      }
      if (seesCosts && payDayDue && payDayDue.people > 0) {
        rows.push({
          key: "pay-day-passed",
          text: `Pay day passed: ${payDayDue.people} ${payDayDue.people === 1 ? "person" : "people"} not paid for ${monthLabel({
            year: payDayDue.forYear,
            month: payDayDue.forMonth,
          })} · ${eur(payDayDue.owedEur)}`,
          // Opens the month that is owed, so the payment is booked to it.
          to: `/app/finances?month=${payDayDue.forYear}-${String(payDayDue.forMonth).padStart(2, "0")}`,
          cta: "Finances",
        });
      }
      if (seesCosts && missingInvoiceCount > 0) {
        rows.push({
          key: "expenses-no-invoice",
          text: `${missingInvoiceCount} one-off ${
            missingInvoiceCount === 1 ? "expense has" : "expenses have"
          } no invoice`,
          to: "/app/finances",
          cta: "Finances",
        });
      }
      if (stockToCheckCount > 0) {
        rows.push({
          key: "shopify-refund-stock",
          text: `${stockToCheckCount} Shopify ${
            stockToCheckCount === 1 ? "refund" : "refunds"
          }: stock to check`,
          to: "/app/sales",
          cta: "Sales",
        });
      }
      if (belowFloorCount > 0) {
        rows.push({
          key: "below-floor",
          text: `${belowFloorCount} ${
            belowFloorCount === 1 ? "sale" : "sales"
          } went below the floor price with no reason given yet`,
          to: "/app/sales",
          cta: "Sales",
        });
      }
      // G-43: learn on Friday that Tuesday will fail — one row per scheduled
      // order that cannot be fulfilled, saying WHAT is short, plus one
      // summary row for the honest blind spots (described bouquets).
      for (const fo of forwardDemand?.orders ?? []) {
        if (fo.verdict !== "SHORT") continue;
        const worst = fo.shortfall.filter((sf) => Number(sf.missingQty) > 0);
        const detail = worst
          .slice(0, 2)
          .map((sf) => `${Number(sf.missingQty)} × ${sf.description}`)
          .join(", ");
        rows.push({
          key: `forward-${fo.orderId}`,
          text: `${fo.code} (${fmtShortDate(fo.scheduledDate)}, ${fo.locationName}) cannot be fulfilled — short ${detail}${
            worst.length > 2 ? ` and ${worst.length - 2} more` : ""
          }`,
          to: "/app/orders",
          cta: "Orders",
        });
      }
      const uncheckable = (forwardDemand?.orders ?? []).filter(
        (fo) => fo.verdict === "UNCHECKABLE",
      );
      if (uncheckable.length > 0) {
        rows.push({
          key: "forward-uncheckable",
          text: `${uncheckable.length} scheduled ${
            uncheckable.length === 1 ? "order" : "orders"
          } cannot be stock-checked yet (described bouquets or free-text lines)`,
          to: "/app/orders",
          cta: "Orders",
        });
      }
      return rows;
    }, [
      seesDispatch,
      problems,
      pendingJobs,
      unconfirmed,
      shopifyNewCount,
      unpreparedOrders,
      orders,
      belowFloorCount,
      stockToCheckCount,
      forwardDemand,
      waitingFactura,
      seesCosts,
      missingInvoiceCount,
      missingDocumentCount,
      payDayDue,
    ]);

  const today = new Date();
  const todayKey = localDayKey(today);
  const monday = mondayOf(today);
  const periodDays = scope === "today" ? { from: todayKey, to: todayKey } : monthDays(scope);
  const salesLink = (locationId?: string) =>
    `/app/sales?from=${periodDays.from}&to=${periodDays.to}${
      locationId ? `&location=${encodeURIComponent(locationId)}` : ""
    }`;
  const shopsInScope = useMemo(
    () => locations.filter((l) => scopeLocationIds.includes(l.id)).map((l) => ({ id: l.id, name: l.name })),
    [locations, scopeLocationIds],
  );
  const scopeLabel = hasRole("OWNER")
    ? `${scopeLocationIds.length} ${scopeLocationIds.length === 1 ? "shop" : "shops"}`
    : (user?.assignedLocation?.name ?? "no shop assigned");

  // G-204: the sidebar's own links for the role, behind "Actions" on a phone.
  // An admin's sidebar has none of these pages (an admin signs in as an owner
  // to run the shop), so an admin gets no menu.
  const actionItems: HomeActionItem[] = hasRole("OWNER")
    ? [
        { to: "/app/orders/build", icon: "buildSend", label: "Build & send order" },
        { to: "/app/orders", icon: "bag", label: "Orders" },
        { to: "/app/inventory", icon: "box", label: "Inventory" },
        { to: "/app/schedule", icon: "calendar", label: "Schedule" },
        { to: "/app/analytics", icon: "chart", label: "Analytics" },
        { to: "/app/couriers", icon: "bike", label: "Couriers" },
      ]
    : hasRole("MANAGER")
      ? [
          { to: "/app/orders/build", icon: "buildSend", label: "Build & send order" },
          { to: "/app/orders", icon: "bag", label: "Orders" },
          { to: "/app/inventory", icon: "box", label: "Inventory" },
          { to: "/app/schedule", icon: "calendar", label: "Schedule" },
        ]
      : hasRole("FLORIST")
        ? [
            { to: "/app/orders", icon: "bag", label: "Orders" },
            { to: "/app/deliveries", icon: "inbox", label: "Incoming stock" },
            { to: "/app/schedule", icon: "calendar", label: "Schedule" },
            { to: "/app/my/shifts", icon: "clock", label: "My shifts" },
          ]
        : [];

  const showByShop = seesMoney && scopedShops != null && scopedShops.length > 1;

  const orderStatusBadge = (status: CustomerOrder["status"]) =>
    status === "READY" || status === "COMPLETED"
      ? "badge success"
      : status === "IN_PREPARATION"
        ? "badge primary"
        : "badge warning";

  return (
    <section className="page-stack home-page">
      {!hasRole("OWNER", "MANAGER", "ADMIN") ? (
        <header className="home-header">
          <h1 className="page-title">Today</h1>
          <p className="home-meta muted">
            {longDate(today)} · {scopeLabel}
          </p>
        </header>
      ) : null}

      {error ? <div className="alert error">{error}</div> : null}

      {seesOwnShifts ? <YourShiftCard shifts={myShifts} todayKey={todayKey} /> : null}

      {/* Needs attention — first on every width: the only block that asks for a person. */}
      <div className="card an-card home-attention-card">
        <div className="an-card-head">
          <strong>
            Needs attention
            {attention.length > 0 ? <span className="badge warning home-count">{attention.length}</span> : null}
          </strong>
        </div>
        {attention.length === 0 ? (
          <p className="muted an-note">Nothing needs you right now.</p>
        ) : (
          <ul className="home-attention">
            {attention.map((row) => (
              <li key={row.key}>
                <span>{row.text}</span>
                <Link to={row.to} className="button plain">
                  {row.cta}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Today's figures (owner + manager). The number stays neutral; the
          hint carries the change. "Sales" opens today's tickets in Sales
          records; "Waste" opens what was wasted, per shop (G-204). */}
      {seesMoney ? (
        <div className={seesCosts ? "stat-tile-row home-tiles" : "stat-tile-row home-tiles home-tiles--three"}>
          <Link to={salesLink()} className="home-tile-link" aria-label="Sales today — open today's sales">
            <StatTile
              label="Sales today"
              value={totals ? eur(totals.revenueEur) : "…"}
              hint={
                totals
                  ? joinNotes(
                      revenueDelta != null
                        ? `${revenueDelta >= 0 ? "+" : ""}${revenueDelta}% vs a normal day`
                        : null,
                      totals.saleCount != null ? `${totals.saleCount} ${totals.saleCount === 1 ? "sale" : "sales"}` : null,
                    ) ?? undefined
                  : undefined
              }
            />
          </Link>
          {seesCosts ? (
            <StatTile
              label={profitToday?.hasCosts ? "Profit after costs" : "Profit before running costs"}
              value={profitToday ? eur(profitToday.valueEur) : "…"}
              tone={profitToday?.valueEur != null && profitToday.valueEur < 0 ? "negative" : "neutral"}
              hint={
                joinNotes(
                  profitToday?.costsFailed
                    ? "could not be loaded"
                    : profitToday?.hasCosts && profitToday.valueEur == null
                      ? "a shop that sold today has no running costs on record"
                      : profitToday?.hasCosts
                        ? `${eur(profitToday.afterVatEur)} margin − ${eur(profitToday.costsEur)} running costs`
                        : profitToday
                          ? "no running costs on record"
                          : null,
                  // WO-B part 5 (OD-198): the cost of goods is their known part.
                  salesWithUnknownCostNote(totals?.unknownCostSaleCount),
                ) ?? undefined
              }
            />
          ) : null}
          <StatTile
            label="Orders due"
            value={ordersDueToday != null ? String(ordersDueToday) : "…"}
            tone={unpreparedOrders.length > 0 ? "warning" : "neutral"}
            hint={unpreparedOrders.length > 0 ? `${unpreparedOrders.length} not started` : undefined}
          />
          <button
            type="button"
            className="home-tile-link"
            onClick={() => setWasteOpen(true)}
            aria-label="Waste today — open today's waste entries"
          >
            <StatTile
              label="Waste"
              value={totals ? eur(totals.wasteEur) : "…"}
              tone={totals?.wasteEur != null && totals.wasteEur > 0 ? "warning" : "neutral"}
              hint={joinNotes("at cost", unknownEntriesNotCountedNote(totals?.wasteUnknownCostEntryCount)) ?? undefined}
            />
          </button>
        </div>
      ) : null}

      {/* G-25 / G-204 — the per-shop table. Only worth the space once there
          is more than one shop; with one it is the tiles again. A table on a
          desk, one card per shop on a phone (the 900 px nav breakpoint). */}
      {showByShop ? (
        <div className="card an-card home-byshop">
          <div className="an-card-head">
            <strong>By shop</strong>
            <label className="home-period">
              <span className="muted">showing</span>
              <select
                value={scope === "today" ? "today" : `${scope.year}-${scope.month}`}
                onChange={(e) => {
                  if (e.target.value === "today") {
                    setScope("today");
                    return;
                  }
                  const [y, m] = e.target.value.split("-").map(Number);
                  setScope({ year: y, month: m });
                }}
              >
                <option value="today">Today</option>
                {Array.from({ length: 12 }, (_, i) => {
                  // G-175 (KI-53): the Barcelona months the server counts, not the browser's.
                  const p = businessMonthsAgo(i);
                  return (
                    <option key={`${p.year}-${p.month}`} value={`${p.year}-${p.month}`}>
                      {monthLabel(p)}
                    </option>
                  );
                })}
              </select>
            </label>
          </div>

          {scopedShops && scopedTotalRevenue === 0 ? (
            <p className="muted an-note">
              {scope === "today" ? "No sales yet today." : `No sales recorded in ${monthLabel(scope)}.`}
            </p>
          ) : null}

          {cardLayout ? (
            <ul className="home-shops">
              {scopedShops!.map((shop) => {
                const unknownSalesNote = salesWithUnknownCostNote(shop.unknownCostSaleCount);
                const unknownWasteNote = unknownEntriesNotCountedNote(shop.wasteUnknownCostEntryCount);
                return (
                  <li key={shop.locationId}>
                    <div className="home-shop-head">
                      <Link to={salesLink(shop.locationId)} className="home-shop-name">
                        {shop.name}
                      </Link>
                      <span className="home-shop-revenue">{eur(shop.revenueEur)}</span>
                    </div>
                    <div
                      className="home-shop-bar"
                      role="presentation"
                      style={{ ["--share" as string]: `${shop.sharePercent.toFixed(1)}%` }}
                    >
                      <span />
                    </div>
                    <div className="home-shop-meta muted">
                      {seesCosts && shop.profitEur != null ? (
                        <span className={shop.profitEur < 0 ? "is-negative" : shop.profitEur > 0 ? "is-positive" : undefined}>
                          {eur(shop.profitEur)} profit
                          <span className="home-shop-vat"> after {eur(shop.runningCostsEur)} costs</span>
                        </span>
                      ) : (
                        <span
                          className={
                            shop.contributionAfterVatEur < 0
                              ? "is-negative"
                              : shop.contributionAfterVatEur > 0
                                ? "is-positive"
                                : undefined
                          }
                        >
                          {eur(shop.contributionAfterVatEur)} margin
                          <span className="home-shop-vat"> after VAT</span>
                        </span>
                      )}
                      {unknownSalesNote ? <span>{unknownSalesNote}</span> : null}
                      {shop.sharePercent > 0 ? <span>{shop.sharePercent.toFixed(0)}% of the total</span> : null}
                      {scope === "today" ? (
                        <span>
                          {shop.ordersDue} {shop.ordersDue === 1 ? "order" : "orders"} due
                        </span>
                      ) : null}
                      {shop.wasteEur > 0 ? <span>{eur(shop.wasteEur)} waste</span> : null}
                      {unknownWasteNote ? <span>{unknownWasteNote}</span> : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="table-scroll">
              <table className="home-shop-table">
                <thead>
                  <tr>
                    <th scope="col">Shop</th>
                    <th scope="col" className="num">Sales</th>
                    <th scope="col" className="home-shop-table-share">Share</th>
                    <th scope="col" className="num">Margin after VAT</th>
                    {seesCosts ? <th scope="col" className="num">{scope === "today" ? "Costs today" : "Costs"}</th> : null}
                    {seesCosts ? <th scope="col" className="num">Profit</th> : null}
                    {scope === "today" ? <th scope="col" className="num">Orders due</th> : null}
                    <th scope="col" className="num">Waste</th>
                  </tr>
                </thead>
                <tbody>
                  {scopedShops!.map((shop) => {
                    const quiet = shop.revenueEur === 0 ? "muted" : undefined;
                    return (
                      <tr key={shop.locationId}>
                        <td>
                          <Link to={salesLink(shop.locationId)} className="home-shop-name">
                            {shop.name}
                          </Link>
                        </td>
                        <td className={`num ${quiet ?? ""}`}>{eur(shop.revenueEur)}</td>
                        <td>
                          <div
                            className="home-shop-bar"
                            role="presentation"
                            style={{ ["--share" as string]: `${shop.sharePercent.toFixed(1)}%` }}
                          >
                            <span />
                          </div>
                        </td>
                        <td className={`num ${quiet ?? ""}`}>
                          {eur(shop.contributionAfterVatEur)}
                          {shop.unknownCostSaleCount > 0 ? (
                            <small className="muted home-cell-note">{salesWithUnknownCostNote(shop.unknownCostSaleCount)}</small>
                          ) : null}
                        </td>
                        {seesCosts ? <td className="num muted">{shop.runningCostsEur == null ? "—" : eur(shop.runningCostsEur)}</td> : null}
                        {seesCosts ? (
                          <td className={`num home-shop-profit ${shop.profitEur != null && shop.profitEur < 0 ? "is-negative" : ""}`}>
                            {shop.profitEur == null ? "—" : eur(shop.profitEur)}
                          </td>
                        ) : null}
                        {scope === "today" ? <td className={`num ${quiet ?? ""}`}>{shop.ordersDue}</td> : null}
                        <td className={`num ${shop.wasteEur > 0 ? "" : "muted"}`}>
                          {eur(shop.wasteEur)}
                          {shop.wasteUnknownCostEntryCount > 0 ? (
                            <small className="muted home-cell-note">{unknownEntriesNotCountedNote(shop.wasteUnknownCostEntryCount)}</small>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {scopedTotals ? (
                  <tfoot>
                    <tr>
                      <td>All shops</td>
                      <td className="num">{eur(scopedTotals.revenueEur)}</td>
                      <td />
                      <td className="num">{eur(scopedTotals.contributionAfterVatEur)}</td>
                      {seesCosts ? <td className="num muted">{scopedTotals.runningCostsEur == null ? "—" : eur(scopedTotals.runningCostsEur)}</td> : null}
                      {seesCosts ? (
                        <td className={`num home-shop-profit ${scopedTotals.profitEur != null && scopedTotals.profitEur < 0 ? "is-negative" : ""}`}>
                          {scopedTotals.profitEur == null ? "—" : eur(scopedTotals.profitEur)}
                        </td>
                      ) : null}
                      {scope === "today" ? <td className="num">{ordersDueToday ?? "…"}</td> : null}
                      <td className="num">{eur(scopedTotals.wasteEur)}</td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          )}

          {scope === "today" && baseline ? (
            <p className="muted an-note">
              A normal trading day this month: {eur(baseline.averageEur)} over {baseline.tradingDays}{" "}
              {baseline.tradingDays === 1 ? "day" : "days"}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* Today's orders beside Today's team on a wide screen; stacked below it. */}
      <div className={seesMoney ? "home-two-col" : "home-two-col home-two-col--single"}>
        <div className="card an-card home-orders-card">
          <div className="an-card-head">
            <strong>
              Today&apos;s orders
              {orders && orders.length > 0 ? <span className="badge muted home-count">{orders.length}</span> : null}
            </strong>
            <Link to="/app/orders" className="button plain">
              Full calendar
            </Link>
          </div>
          {orders == null ? (
            <p className="muted an-note">Loading…</p>
          ) : orders.length === 0 ? (
            <p className="muted an-note">Nothing scheduled for today.</p>
          ) : (
            <ul className="home-orders">
              {orders.map((order) => (
                <li key={order.id}>
                  <button type="button" className="home-order-open" onClick={() => setDetailOrder(order)}>
                    <span className="home-order-time">{timeLabel(order)}</span>
                    <span className="home-order-main">
                      <strong>{order.customerName}</strong>
                      <span className="muted">
                        {" "}
                        · {order.code} · {order.fulfillment === "PICKUP" ? "Pickup" : "Delivery"}
                        {scopeLocationIds.length > 1 ? ` · ${order.location.name}` : ""}
                        {order.totalEur ? ` · ${eur(order.totalEur)}` : " · no price yet"}
                      </span>
                    </span>
                    <span className={orderStatusBadge(order.status)}>{ORDER_STATUS_LABELS[order.status]}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* G-24 Today's team — owner + manager only (a florist's home carries
            no money). G-204: each person's sales; commission left off the
            home page; fault waste only when there is any. */}
        {seesMoney ? (
          <div className="card an-card home-team-card">
            <div className="an-card-head">
              <strong>Today&apos;s team</strong>
              <Link to="/app/schedule" className="button plain">
                Schedule
              </Link>
            </div>
            {team == null ? (
              <p className="muted an-note">Loading…</p>
            ) : team.length === 0 ? (
              <p className="muted an-note">Nobody is scheduled today.</p>
            ) : (
              <ul className="home-team">
                {team.map((person) => (
                  <li key={person.userId}>
                    <span className="home-team-who">
                      <strong>{person.fullName}</strong>
                      <small className="muted">
                        {hhmm(person.plannedStart)}–{hhmm(person.plannedEnd)}
                        {person.actualStart ? ` · in since ${hhmm(person.actualStart)}` : ""}
                        {person.actualEnd ? ` · out ${hhmm(person.actualEnd)}` : ""}
                      </small>
                    </span>
                    <span className="home-team-figs">
                      <span>
                        <small className="muted">Sales</small>
                        <strong>{eur(person.salesEur)}</strong>
                      </span>
                      {person.faultWasteEur != null && person.faultWasteEur > 0 ? (
                        <span>
                          <small className="muted">Fault waste</small>
                          <strong className="is-negative">{eur(person.faultWasteEur)}</strong>
                        </span>
                      ) : null}
                    </span>
                    {person.overdue ? (
                      <span className="badge warning">Still open</span>
                    ) : person.actualStart ? null : (
                      <span className="badge muted">Not clocked in</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </div>

      {seesOwnShifts ? <YourWeekCard shifts={myShifts} todayKey={todayKey} monday={monday} /> : null}

      {detailOrder ? (
        <OrderDetailModal
          order={detailOrder}
          onClose={() => setDetailOrder(null)}
          onChanged={async () => {
            await refresh();
            const updated = await api
              .getCustomerOrder(detailOrder.id)
              .catch(() => null);
            if (updated) setDetailOrder(updated);
          }}
        />
      ) : null}

      {wasteOpen ? (
        <WasteTodayModal shops={shopsInScope} from={todayKey} to={todayKey} onClose={() => setWasteOpen(false)} />
      ) : null}

      <HomeActions items={actionItems} />
    </section>
  );
}
